<script setup>
import { HrBtn } from '../primitives';

/**
 * Low-emphasis text button for tertiary or auxiliary actions.
 *
 * @status recommended
 * @group actions
 * @useWhen Inline or auxiliary actions that should not compete visually with primary or secondary buttons
 * @related HrSupportingBtn
 * @related HrDismissBtn
 * @a11y Native button; activates on Enter and Space.
 */
defineOptions({ name: 'HrSubtleBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

defineProps({
  /**
   * Button text label. When null the default slot provides the content.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Icon displayed to the left of the label. */
  icon: {
    type: String,
    default: null,
  },
  /** Icon displayed to the right of the label. */
  iconRight: {
    type: String,
    default: null,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading spinner in place of the label. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = { flat: true, color: 'primary' };
</script>
<template>
  <hr-btn
    variant="text"
    :legacy-props="legacyProps"
    v-bind="$props"
    @click="emit('click', $event)"
  >
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
